// Extracted section components and form types for NewPatientModal.

import type { JSX } from "react";
import { FormField } from "./FormField.js";
import { SelectField } from "./SelectField.js";
import { PhoneField } from "./PhoneField.js";
import { SwitchToggle } from "./SwitchToggle.js";
import { PRIMARY_LANGUAGES } from "../lib/clinical-options.js";
import type { PatientFormErrors } from "../lib/patient-validation.js";

export interface PatientForm {
  full_name: string; address: string; primary_phone: string;
  emergency_contact_name: string; emergency_contact_phone: string;
  age: string; biological_sex: string; city: string; state: string;
  primary_language: string; living_situation: string;
  chronic_conditions: string[];
  baseline_mobility: string; baseline_cognitive_status: string; baseline_appetite: string;
  date_of_birth: string; fall_history_prior_12m: string;
  baseline_continence: string; primary_diagnosis: string;
  baseline_bp_systolic: string; baseline_bp_diastolic: string; baseline_heart_rate: string; baseline_blood_glucose: string; weight_kg: string;
  enrolment_date: string; plan_tier: string;
  blood_type: string; contraindications: string;
  gp_name: string; gp_phone: string;
  has_health_insurance: boolean; household_paid_help_present: boolean;
  nsofar_sync_consent: boolean; nsofar_tagged: boolean; nsofar_sync_opt_in: boolean;
  recent_hospitalisation_prior_12m: boolean; recent_hospitalisation_reason: string;
}

export const INIT: PatientForm = {
  full_name: "", address: "", primary_phone: "", emergency_contact_name: "",
  emergency_contact_phone: "", age: "", biological_sex: "not_disclosed", city: "", state: "",
  primary_language: "hindi", living_situation: "alone", chronic_conditions: [],
  baseline_mobility: "independent", baseline_cognitive_status: "alert_oriented", baseline_appetite: "good",
  date_of_birth: "", fall_history_prior_12m: "none",
  baseline_continence: "continent", primary_diagnosis: "",
  baseline_bp_systolic: "", baseline_bp_diastolic: "", baseline_heart_rate: "", baseline_blood_glucose: "", weight_kg: "",
  enrolment_date: new Date().toISOString().slice(0, 10), plan_tier: "silver",
  blood_type: "unknown", contraindications: "",
  gp_name: "", gp_phone: "", has_health_insurance: false, household_paid_help_present: false,
  nsofar_sync_consent: false, nsofar_tagged: false, nsofar_sync_opt_in: false,
  recent_hospitalisation_prior_12m: false, recent_hospitalisation_reason: "",
};

const SEX_OPTS    = [{ value: "male", label: "Male" }, { value: "female", label: "Female" }, { value: "not_disclosed", label: "Not disclosed" }];
const LIVING_OPTS = [
  { value: "alone", label: "Alone" }, { value: "with_spouse", label: "With spouse" },
  { value: "with_family_member", label: "With family" }, { value: "with_paid_help", label: "With paid help" },
  { value: "assisted_living", label: "Assisted living" },
];
const BLOOD_TYPE_OPTS = [
  { value: "unknown", label: "Unknown" },
  { value: "A+", label: "A+" }, { value: "A-", label: "A-" },
  { value: "B+", label: "B+" }, { value: "B-", label: "B-" },
  { value: "AB+", label: "AB+" }, { value: "AB-", label: "AB-" },
  { value: "O+", label: "O+" }, { value: "O-", label: "O-" },
];

type F = PatientForm;
type OnChange = (patch: Partial<F>) => void;
type Errors = PatientFormErrors;

export function PersonalSection({ f, on, e }: { f: F; on: OnChange; e: Errors }): JSX.Element {
  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Personal details</p>
      <FormField label="Full name" value={f.full_name} onChange={(v) => on({ full_name: v })} required error={e.full_name} placeholder="e.g. Rahul Sharma" />
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Age" type="number" value={f.age} onChange={(v) => on({ age: v })} required error={e.age} placeholder="e.g. 72" />
        <SelectField label="Sex" value={f.biological_sex} onChange={(v) => on({ biological_sex: v })} options={SEX_OPTS} required />
      </div>
      <PhoneField label="Phone" value={f.primary_phone} onChange={(v) => on({ primary_phone: v })} required error={e.primary_phone} />
      <FormField label="Emergency contact name" value={f.emergency_contact_name} onChange={(v) => on({ emergency_contact_name: v })} required error={e.emergency_contact_name} />
      <PhoneField label="Emergency contact phone" value={f.emergency_contact_phone} onChange={(v) => on({ emergency_contact_phone: v })} required error={e.emergency_contact_phone} />
    </div>
  );
}

const KNOWN_LANGUAGES = PRIMARY_LANGUAGES.map((l) => l.value);

export function AddressSection({ f, on, e }: { f: F; on: OnChange; e: Errors }): JSX.Element {
  const isOtherLang = f.primary_language !== "" && !(KNOWN_LANGUAGES as string[]).includes(f.primary_language);
  const selectLangValue = isOtherLang ? "Other" : f.primary_language;

  function handleLangChange(v: string): void {
    if (v === "Other") on({ primary_language: "" }); // blank → user types below
    else on({ primary_language: v });
  }

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-4">Location</p>
      <FormField label="Full address" value={f.address} onChange={(v) => on({ address: v })} required error={e.address} placeholder="e.g. 12 MG Road, Indiranagar" />
      <div className="grid grid-cols-2 gap-3">
        <FormField label="City" value={f.city} onChange={(v) => on({ city: v })} required error={e.city} placeholder="e.g. Bengaluru" />
        <FormField label="State" value={f.state} onChange={(v) => on({ state: v })} required error={e.state} placeholder="e.g. Karnataka" />
      </div>
      <SelectField
        label="Primary language"
        value={selectLangValue}
        onChange={handleLangChange}
        options={PRIMARY_LANGUAGES as unknown as { value: string; label: string }[]}
      />
      {(selectLangValue === "Other" || isOtherLang) && (
        <FormField
          label="Specify language"
          value={isOtherLang ? f.primary_language : ""}
          onChange={(v) => on({ primary_language: v })}
          placeholder="e.g. Tulu, Konkani, Sindhi…"
        />
      )}
      <SelectField label="Living situation" value={f.living_situation} onChange={(v) => on({ living_situation: v })} options={LIVING_OPTS} required />
    </div>
  );
}

export function MedicalDetailsSection({ f, on }: { f: F; on: OnChange }): JSX.Element {
  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-4">Medical details</p>
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Blood type" value={f.blood_type} onChange={(v) => on({ blood_type: v })} options={BLOOD_TYPE_OPTS} />
        <FormField label="GP name" value={f.gp_name} onChange={(v) => on({ gp_name: v })} placeholder="e.g. Dr. Patel" />
      </div>
      <PhoneField label="GP phone" value={f.gp_phone} onChange={(v) => on({ gp_phone: v })} />
      <FormField
        label="Contraindications / Allergies"
        value={f.contraindications}
        onChange={(v) => on({ contraindications: v })}
        placeholder="e.g. Penicillin, NSAIDs"
      />
      <SwitchToggle label="Nsofar patient?" checked={f.nsofar_tagged} onChange={(v) => on({ nsofar_tagged: v })} />
      {f.nsofar_tagged && <>
        <SwitchToggle label="Nsofar sync consent" checked={f.nsofar_sync_consent} onChange={(v) => on({ nsofar_sync_consent: v })} />
        <SwitchToggle label="Nsofar data opt-in" checked={f.nsofar_sync_opt_in} onChange={(v) => on({ nsofar_sync_opt_in: v })} />
      </>}
    </div>
  );
}
